import { useShouldShowMaintenanceBanner } from '@ui/components/Layout/hooks/useShouldShowMaintenanceBanner';
import { useDataChainId } from '@ui/hooks/datachain';
import { useTradeOrders } from '@ui/hooks/trade/useTradeOrders';
import { useBreakpoint } from '@ui/hooks/useBreakPoint';
import { Skeleton } from '@ui/lib/park/ui/skeleton';
import {
  getActiveDecreaseOrdersByPositionHash,
  getActiveSlOrders,
  getActiveTpOrders,
  getDecreaseOrderTriggerPrice,
} from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/selectors';
import { useDecreaseOrders } from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/useDecreaseOrders';
// eslint-disable-next-line import/no-restricted-paths
import { BottomTabs } from '@ui/views/Trade/BottomTabs';
import dynamic from 'next/dynamic';
import { memo, useMemo } from 'react';
import { css } from 'styled-system/css';
import { Box, Flex, VStack } from 'styled-system/jsx';
import { useAccount } from 'wagmi';

import { useIsTradePageContainerDesktop } from '../hooks/useIsTradePageContainerDesktop';
import { useTradingPair } from '../store/useTradingPair';
import { PositionTable, PriceChartCollapse } from './PriceChart';
import { ChartLoadingCover } from './PriceChart/PriceChart';
import {
  defaultChartProps,
  disabledFeaturesOnMobile,
  enabledFeaturesOnMobile,
} from './PriceChart/constants';
import { DrawLines } from './PriceChart/DrawLines';
import { useChartLineStore } from './PriceChart/useChartLineStore';
import { createDatafeed, load } from './PriceChart/utils';
import { TradeModule } from './TradeModule/TradeModule';
import { TradeModuleProvider } from './TradeModuleConfirm/hooks/useTradeModuleContext';

const LazyChart = dynamic(
  () => import('./PriceChart/PriceChart').then((v) => v.PriceChart),
  { ssr: true },
);

const skeletonCss = css.raw({
  w: 'full',
  // desktop: fill the flex column so the chart meets the positions strip
  // with no dead band (the old fixed 66svh left a gap on tall screens)
  h: {
    base: '100svw',
    desktop: 'auto',
  },
  flex: {
    base: 'none',
    desktop: '1 1 0%',
  },
  minH: {
    desktop: 0,
  },
  bg: 'v2.bg.panel',
});
export const Body = memo(() => {
  const tradingPair = useTradingPair((s) => s.useTradingPair());
  const isMobile = useBreakpoint((w, b) => w < b.tablet);
  const chainId = useDataChainId();
  const { address } = useAccount();
  const mainChartDataFeed = useMemo(
    () => createDatafeed({ chartId: 'trade-page-main-chart', chainId }),
    [chainId],
  );
  const chartConfig = useMemo(() => {
    if (!isMobile.hasResizeTriggered) {
      return;
    }

    return {
      enabled_features: isMobile.value
        ? defaultChartProps.enabled_features?.concat(enabledFeaturesOnMobile)
        : defaultChartProps.enabled_features,
      disabled_features: isMobile.value
        ? defaultChartProps?.disabled_features?.concat(disabledFeaturesOnMobile)
        : defaultChartProps.disabled_features,
      saved_data: load({ symbol: tradingPair.baseSymbol }),
    };
  }, [isMobile.hasResizeTriggered, isMobile.value, tradingPair.baseSymbol]);

  const shouldShowMaintenanceBanner = useShouldShowMaintenanceBanner();

  return (
    // Conventional page-scroll terminal: a long positions list extends the
    // document and the WINDOW scrolls — no inner scrollbars on the left side
    <Flex
      minH={{
        base: 'layout.contentHeight',
        desktop: shouldShowMaintenanceBanner
          ? 'layout.contentHeightWithAnnouncement'
          : 'layout.contentHeight',
      }}
    >
      {/* <PriceChartCollapse> */}
      <VStack
        gap={0}
        flex={1}
        minW={0}
        borderRight="1px solid"
        borderRightColor="v2.border.default"
      >
        <Box
          w="full"
          // fixed viewport share: contentHeight (100vh - 48 nav - 56 marketbar
          // [- 70 banner]) minus the 260px tables floor — the chart never
          // shrinks; a long list pushes the page taller instead
          h={{
            desktop: shouldShowMaintenanceBanner
              ? 'calc(100vh - 434px)'
              : 'calc(100vh - 364px)',
          }}
          flexShrink={0}
          minH={{ base: 'auto', desktop: '400px' }}
          display="flex"
          flexDir="column"
        >
          {chartConfig ? (
            <LazyChart
              id="trade-page-main-chart"
              datafeed={mainChartDataFeed}
              tradingPair={tradingPair}
              chartProps={chartConfig}
              w="full"
              h="full"
              skeletonCss={skeletonCss}
            >
              <DrawLinesWithData />
            </LazyChart>
          ) : (
            // identical visual to the chart's own loading cover — mount
            // gaps and remounts read as one continuous loading animation
            <ChartLoadingCover css={skeletonCss} />
          )}
        </Box>
        {/* Hyperliquid/Ostium proportions: the bottom strip is a fixed ~1/3
            share with a floor, substantial even when empty — never a sliver */}
        <Box
          w="full"
          flexShrink={0}
          // mockup terminal grid: positions row = minmax(260px, auto) — grows
          // naturally; overflow is handled by the column scroll above
          h={{ desktop: 'auto' }}
          minH={{ desktop: '260px' }}
          borderTop={{ desktop: '1px solid' }}
          borderTopColor={{ desktop: 'v2.border.default' }}
        >
          <BottomTabs userAddress={address} />
        </Box>
      </VStack>

      {/* <PositionTable /> */}
      {/* </PriceChartCollapse> */}
      <TradeModuleDesktopContainer />
    </Flex>
  );
});
Body.displayName = 'Trade.Body';
const TradeModuleDesktopContainer = () => {
  const { value: isDesktop, isPending } = useIsTradePageContainerDesktop();
  const shouldShowMaintenanceBanner = useShouldShowMaintenanceBanner();

  if (isDesktop || isPending)
    return (
      // for mobile close tradeModuleModal when open confirm modal
      <Box
        display={{ base: 'none', desktop: 'block' }}
        w="360px"
        flexShrink={0}
        // sticky: stays usable while the page scrolls through a long
        // positions list; expanding TP/SL etc. scrolls this panel only
        position="sticky"
        top={
          shouldShowMaintenanceBanner
            ? 'layout.topNavBarWithAnnouncementHeight'
            : 'layout.topNavBarHeight'
        }
        h={
          shouldShowMaintenanceBanner
            ? 'layout.contentHeightWithAnnouncement'
            : 'layout.contentHeight'
        }
        overflowY="auto"
        css={{
          overscrollBehavior: 'contain',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': {
            background: 'var(--colors-v2-border-default)',
          },
        }}
      >
        <TradeModuleProvider>
          <TradeModule />
        </TradeModuleProvider>
      </Box>
    );

  // avoid wasteful desktop trade module render logic running in mobile
  return <></>;
};

const DrawLinesWithData = () => {
  const tradingPair = useTradingPair((s) => s.useTradingPair());
  const { position } = useChartLineStore();
  const { data: decreaseOrders } = useDecreaseOrders();
  const activeDecreaseOrdersByPositionHash = useMemo(
    () => getActiveDecreaseOrdersByPositionHash(decreaseOrders),
    [decreaseOrders],
  );

  const { data: orders } = useTradeOrders();
  const selectedOrders = useMemo(() => {
    // include degen pair orders
    return orders.filter((o) => o.pair.baseName === tradingPair.baseName);
  }, [orders, tradingPair.baseName]);

  const selectedPositions = useMemo(() => {
    if (!position) return [];

    return [position];
  }, [position]);

  const tpsl = useMemo(() => {
    return (selectedPositions ?? []).map((pos) => {
      const activeDecreaseOrders = activeDecreaseOrdersByPositionHash[pos.id];
      const tpOrders = getActiveTpOrders(activeDecreaseOrders);
      const slOrders = getActiveSlOrders(activeDecreaseOrders);

      return {
        isLong: pos.isLong,
        takeProfit: pos.isMergedPosition
          ? getDecreaseOrderTriggerPrice(tpOrders[0])
          : pos.takeProfit.isZero()
            ? undefined
            : pos.takeProfit,
        stopLoss: pos.isMergedPosition
          ? getDecreaseOrderTriggerPrice(slOrders[0])
          : pos.stopLoss.isZero()
            ? undefined
            : pos.stopLoss,
      };
    });
  }, [activeDecreaseOrdersByPositionHash, selectedPositions]);

  return (
    <DrawLines
      tradingPair={tradingPair}
      orders={selectedOrders}
      positions={selectedPositions}
      tpSl={tpsl}
    />
  );
};
